import { useEffect, useRef, useCallback } from 'react';
import NotificationService, { NotificationServiceTopic } from '../api/NotificationService';

// Key used for cross-tab communication when Stripe onboarding completes
export const STRIPE_ONBOARDING_COMPLETE_KEY = "stripe_onboarding_complete";

interface UseStripePaymentRefreshOptions {
  /** Callback to refresh data when user returns from Stripe */
  onRefresh?: () => void | Promise<void>;
  /** Polling interval in milliseconds (default: 5000ms) */
  pollingInterval?: number;
  /** Maximum polling duration in milliseconds (default: 5 minutes) */
  maxPollingDuration?: number;
  /** Shift ID to publish notification for */
  shiftId?: string;
}

/**
 * Production-grade hook to handle automatic refresh after Stripe payment.
 * 
 * Uses two strategies:
 * 1. Window focus/visibility detection - refreshes when user returns to the app
 * 2. Polling - periodically checks for payment status updates
 * 
 * Usage:
 * ```tsx
 * const { startStripePaymentWatch, stopStripePaymentWatch } = useStripePaymentRefresh({
 *   onRefresh: () => setRefreshTrigger(prev => prev + 1),
 *   shiftId: paymentShift?.id,
 * });
 * 
 * // Call when opening Stripe window
 * window.open(stripeUrl, '_blank');
 * startStripePaymentWatch();
 * ```
 */
export const useStripePaymentRefresh = (options: UseStripePaymentRefreshOptions = {}) => {
  const {
    onRefresh,
    pollingInterval = 5000,
    maxPollingDuration = 5 * 60 * 1000, // 5 minutes
    shiftId,
  } = options;

  const isWatchingRef = useRef(false);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusCountRef = useRef(0);
  const lastRefreshTimeRef = useRef<number>(0);
  
  // Store callbacks in refs to avoid stale closures
  const onRefreshRef = useRef(onRefresh);
  const shiftIdRef = useRef(shiftId);
  
  // Update refs when props change
  useEffect(() => {
    onRefreshRef.current = onRefresh;
    shiftIdRef.current = shiftId;
  }, [onRefresh, shiftId]);

  const triggerRefresh = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
    
    console.log('[StripePaymentRefresh] triggerRefresh called');
    console.log('[StripePaymentRefresh] isWatchingRef.current:', isWatchingRef.current);
    console.log('[StripePaymentRefresh] timeSinceLastRefresh:', timeSinceLastRefresh, 'ms');
    console.log('[StripePaymentRefresh] onRefreshRef.current exists:', !!onRefreshRef.current);
    
    // Debounce: only trigger if at least 2 seconds since last refresh
    if (timeSinceLastRefresh < 2000) {
      console.log('[StripePaymentRefresh] Debounced - skipping refresh');
      return;
    }
    lastRefreshTimeRef.current = now;
    
    console.log('[StripePaymentRefresh] Triggering refresh...');
    
    // Publish notification to refresh shift data
    if (shiftIdRef.current) {
      NotificationService.publish(NotificationServiceTopic.ShiftChanged, shiftIdRef.current);
    }
    
    // Also call custom onRefresh if provided
    if (onRefreshRef.current) {
      try {
        onRefreshRef.current();
      } catch (error) {
        console.error('[StripePaymentRefresh] Error during refresh:', error);
      }
    }
  }, []);

  const cleanup = useCallback(() => {
    // Clear polling timer
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    // Clear max duration timer
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
  }, []);

  const stopStripePaymentWatch = useCallback(() => {
    if (!isWatchingRef.current) return;
    
    console.log('[StripePaymentRefresh] Stopping payment watch');
    isWatchingRef.current = false;
    focusCountRef.current = 0;
    cleanup();
  }, [cleanup]);

  const startStripePaymentWatch = useCallback(() => {
    if (isWatchingRef.current) {
      console.log('[StripePaymentRefresh] Already watching');
      return;
    }

    console.log('[StripePaymentRefresh] Starting payment watch');
    console.log('[StripePaymentRefresh] onRefreshRef.current exists:', !!onRefreshRef.current);
    isWatchingRef.current = true;
    focusCountRef.current = 0;
    // Don't reset lastRefreshTimeRef here - this was blocking immediate refreshes
    // lastRefreshTimeRef.current = Date.now();

    // Note: We only use focus/visibility events, no polling
    // Polling was causing continuous loading issues

    // Auto-stop after max duration
    maxDurationTimerRef.current = setTimeout(() => {
      console.log('[StripePaymentRefresh] Max duration reached, stopping watch');
      stopStripePaymentWatch();
    }, maxPollingDuration);

  }, [maxPollingDuration, stopStripePaymentWatch]);

  // Handle visibility change - trigger refresh when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      console.log('[StripePaymentRefresh] visibilitychange event fired, visibilityState:', document.visibilityState);
      console.log('[StripePaymentRefresh] isWatchingRef.current:', isWatchingRef.current);
      
      if (!isWatchingRef.current) {
        console.log('[StripePaymentRefresh] Not watching, ignoring visibility change');
        return;
      }
      
      if (document.visibilityState === 'visible') {
        console.log('[StripePaymentRefresh] Page became visible, triggering refresh');
        focusCountRef.current += 1;
        triggerRefresh();

        // After 5 visibility changes, assume user is done and stop watching
        if (focusCountRef.current >= 5) {
          console.log('[StripePaymentRefresh] Max focus count reached, stopping watch');
          stopStripePaymentWatch();
        }
      }
    };

    const handleWindowFocus = () => {
      console.log('[StripePaymentRefresh] focus event fired');
      console.log('[StripePaymentRefresh] isWatchingRef.current:', isWatchingRef.current);
      
      if (!isWatchingRef.current) {
        console.log('[StripePaymentRefresh] Not watching, ignoring focus');
        return;
      }
      console.log('[StripePaymentRefresh] Window focused, triggering refresh');
      triggerRefresh();
    };

    // Handle localStorage events from other tabs (e.g., Stripe onboarding complete)
    const handleStorageChange = (event: StorageEvent) => {
      console.log('[StripePaymentRefresh] storage event fired, key:', event.key);
      
      if (event.key === STRIPE_ONBOARDING_COMPLETE_KEY) {
        console.log('[StripePaymentRefresh] Stripe onboarding complete signal received!');
        console.log('[StripePaymentRefresh] isWatchingRef.current:', isWatchingRef.current);
        
        // Always trigger refresh when we get the onboarding complete signal, even if not actively watching
        // This handles the case where the user completes Stripe setup in another tab
        triggerRefresh();
        
        // Clean up the localStorage key
        try {
          localStorage.removeItem(STRIPE_ONBOARDING_COMPLETE_KEY);
        } catch (e) {
          console.error('[StripePaymentRefresh] Failed to clean up localStorage:', e);
        }
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [triggerRefresh, stopStripePaymentWatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    startStripePaymentWatch,
    stopStripePaymentWatch,
    isWatching: isWatchingRef.current,
  };
};

export default useStripePaymentRefresh;
